import * as React from 'react';
import { cn } from '../utils/cn';

const baseInputStyles =
  'w-full bg-white text-foreground border border-input rounded-sm px-3 font-body ' +
  'placeholder:text-muted ' +
  'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary ' +
  'disabled:opacity-40 disabled:cursor-not-allowed ' +
  'transition-colors duration-fast ease-smooth';

const sizeStyles: Record<string, string> = {
  large: 'h-12 text-base',
  default: 'h-10 text-sm',
  small: 'h-8 text-xs',
};

// ─── Input ──────────────────────────────────────────────────────────────

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /** Input size */
  size?: 'large' | 'default' | 'small';
  /** Prefix element inside the input */
  prefix?: React.ReactNode;
  /** Suffix element inside the input */
  suffix?: React.ReactNode;
  /** Allow clearing the input */
  allowClear?: boolean;
  /** Antd compat: called when Enter is pressed */
  onPressEnter?: (e?: any) => void;
  /** Antd compat: element rendered after the input field */
  addonAfter?: React.ReactNode;
  /** Antd compat: element rendered before the input field */
  addonBefore?: React.ReactNode;
}

const InputBase = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, size = 'default', prefix, suffix, allowClear, style, onPressEnter, addonAfter: _addonAfter, addonBefore: _addonBefore, ...props }, ref) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onPressEnter?.(e);
      props.onKeyDown?.(e);
    };

    if (prefix || suffix) {
      return (
        <div className={cn('relative flex items-center', className)} style={style}>
          {prefix && (
            <span className="absolute left-3 text-muted">{prefix}</span>
          )}
          <input
            ref={ref}
            className={cn(
              baseInputStyles,
              sizeStyles[size],
              prefix && 'pl-9',
              suffix && 'pr-9'
            )}
            {...props}
            onKeyDown={handleKeyDown}
          />
          {suffix && (
            <span className="absolute right-3 text-muted">{suffix}</span>
          )}
        </div>
      );
    }

    return (
      <input
        ref={ref}
        className={cn(baseInputStyles, sizeStyles[size], className)}
        style={style}
        {...props}
        onKeyDown={handleKeyDown}
      />
    );
  }
);
InputBase.displayName = 'Input';

// ─── TextArea ───────────────────────────────────────────────────────────

export interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Number of visible text rows */
  rows?: number;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, rows = 4, style, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          baseInputStyles,
          'py-2 min-h-[80px] resize-y',
          className
        )}
        style={style}
        {...props}
      />
    );
  }
);
TextArea.displayName = 'TextArea';


// ─── Password ───────────────────────────────────────────────────────────

export interface PasswordProps extends Omit<InputProps, 'type'> {}

export const Password = React.forwardRef<HTMLInputElement, PasswordProps>(
  ({ className, size = 'default', style, prefix: _prefix, suffix: _suffix, allowClear: _allowClear, onPressEnter, addonAfter: _addonAfter, addonBefore: _addonBefore, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onPressEnter?.(e);
      props.onKeyDown?.(e);
    };

    return (
      <div className={cn('relative flex items-center', className)} style={style}>
        <input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cn(baseInputStyles, sizeStyles[size], 'pr-10')}
          {...props}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="absolute right-3 text-muted hover:text-foreground transition-colors duration-fast p-0! h-auto! tracking-normal! normal-case! border-0! bg-transparent!"
          onClick={() => setVisible(!visible)}
          tabIndex={-1}
          aria-label={visible ? 'Dölj lösenord' : 'Visa lösenord'}
        >
          {visible ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    );
  }
);
Password.displayName = 'Password';

// Compound component for antd compat: Input.TextArea, Input.Password
export const Input = Object.assign(InputBase, {
  TextArea,
  Password,
});
